import { z } from 'zod';
import type { Schemas } from '../../../api/client';

export type ReminderRule = Schemas['ReminderRule'];
export type ReminderKind = ReminderRule['kind'];
export type Channel = ReminderRule['channels'][number];

export const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'IN_APP', label: 'In-app' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

interface KindInfo {
  label: string;
  description: string;
  /** How offsetDays is read for this kind. */
  timing: 'before' | 'after' | 'none';
  /** The document has a customer/supplier that can be messaged. */
  contact: boolean;
}

export const KINDS: Record<ReminderKind, KindInfo> = {
  INVOICE_DUE_SOON: {
    label: 'Invoice due soon',
    description: 'Before a customer invoice falls due',
    timing: 'before',
    contact: true,
  },
  INVOICE_OVERDUE: {
    label: 'Invoice overdue',
    description: 'When a customer invoice is past due',
    timing: 'after',
    contact: true,
  },
  BILL_DUE_SOON: {
    label: 'Supplier bill due soon',
    description: 'Before a supplier bill falls due',
    timing: 'before',
    contact: true,
  },
  BILL_OVERDUE: {
    label: 'Supplier bill overdue',
    description: 'When a supplier bill is past due',
    timing: 'after',
    contact: true,
  },
  LOW_STOCK: {
    label: 'Low stock',
    description: 'When stock falls to the minimum level',
    timing: 'none',
    contact: false,
  },
  QUOTATION_EXPIRING: {
    label: 'Quotation expiring',
    description: 'Before a quotation’s validity ends',
    timing: 'before',
    contact: true,
  },
  PO_OVERDUE: {
    label: 'Purchase order overdue',
    description: 'When goods are late from a supplier',
    timing: 'after',
    contact: true,
  },
  SUBSCRIPTION_EXPIRING: {
    label: 'Subscription expiring',
    description: 'Before the shop’s subscription ends',
    timing: 'before',
    contact: false,
  },
};

export const KIND_OPTIONS = (Object.keys(KINDS) as ReminderKind[]).map((k) => ({
  value: k,
  label: KINDS[k].label,
}));

/** "3 days after the due date", "On the due date", "Every 7 days" … */
export function describeTiming(
  rule: Pick<ReminderRule, 'kind' | 'offsetDays' | 'repeatEveryDays'>,
): string {
  const info = KINDS[rule.kind];
  const days = (n: number) => `${String(n)} day${n === 1 ? '' : 's'}`;
  const anchor =
    rule.kind === 'QUOTATION_EXPIRING'
      ? 'expiry'
      : rule.kind === 'PO_OVERDUE'
        ? 'expected date'
        : rule.kind === 'SUBSCRIPTION_EXPIRING'
          ? 'end date'
          : 'due date';
  let when: string;
  if (info.timing === 'none') when = 'When stock is at or below minimum';
  else if (rule.offsetDays === 0) when = `On the ${anchor}`;
  else when = `${days(rule.offsetDays)} ${info.timing} the ${anchor}`;
  const repeat = rule.repeatEveryDays ? ` · repeats every ${days(rule.repeatEveryDays)}` : '';
  return when + repeat;
}

const optionalInt = (min: number) =>
  z
    .union([z.number(), z.nan(), z.null()])
    .transform((v) => (v === null || Number.isNaN(v) ? null : v))
    .refine((v) => v === null || (Number.isInteger(v) && v >= min && v <= 365), {
      message: `Enter a whole number from ${String(min)} to 365`,
    });

export const ruleFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the rule a name').max(120, 'At most 120 characters'),
    kind: z.enum(Object.keys(KINDS) as [ReminderKind, ...ReminderKind[]]),
    isActive: z.boolean(),
    offsetDays: z
      .number({ error: 'Enter a number of days' })
      .int('Whole days only')
      .min(0, 'Cannot be negative')
      .max(365, 'At most 365 days'),
    repeatEveryDays: optionalInt(1),
    channels: z
      .array(z.enum(['IN_APP', 'EMAIL', 'SMS', 'WHATSAPP']))
      .min(1, 'Pick at least one channel'),
    recipientRoleIds: z.array(z.string()),
    recipientUserIds: z.array(z.string()),
    notifyContact: z.boolean(),
    template: z.string().max(1000, 'At most 1000 characters'),
  })
  .superRefine((v, ctx) => {
    const info = KINDS[v.kind];
    const internal = v.recipientRoleIds.length + v.recipientUserIds.length > 0;
    const outbound = v.channels.some((c) => c !== 'IN_APP');
    if (info.timing === 'after' && v.offsetDays < 1)
      ctx.addIssue({
        code: 'custom',
        path: ['offsetDays'],
        message: 'Overdue reminders need at least 1 day',
      });
    if (v.notifyContact && !info.contact)
      ctx.addIssue({
        code: 'custom',
        path: ['notifyContact'],
        message: 'This kind of reminder has no customer or supplier to notify',
      });
    if (v.notifyContact && !outbound)
      ctx.addIssue({
        code: 'custom',
        path: ['channels'],
        message: 'Messaging the contact needs Email, SMS or WhatsApp',
      });
    if (!internal && !v.notifyContact)
      ctx.addIssue({
        code: 'custom',
        path: ['recipientRoleIds'],
        message: 'Choose at least one recipient',
      });
    else if (v.channels.includes('IN_APP') && !internal)
      ctx.addIssue({
        code: 'custom',
        path: ['recipientRoleIds'],
        message: 'In-app notifications need at least one role or staff member',
      });
  });

export type RuleFormInput = z.input<typeof ruleFormSchema>;
export type RuleFormValues = z.output<typeof ruleFormSchema>;

export function ruleDefaults(rule?: ReminderRule | null): RuleFormInput {
  if (rule)
    return {
      name: rule.name,
      kind: rule.kind,
      isActive: rule.isActive,
      offsetDays: rule.offsetDays,
      repeatEveryDays: rule.repeatEveryDays,
      channels: [...rule.channels],
      recipientRoleIds: [...rule.recipientRoleIds],
      recipientUserIds: [...rule.recipientUserIds],
      notifyContact: rule.notifyContact,
      template: rule.template ?? '',
    };
  return {
    name: '',
    kind: 'INVOICE_OVERDUE',
    isActive: true,
    offsetDays: 1,
    repeatEveryDays: 7,
    channels: ['IN_APP'],
    recipientRoleIds: [],
    recipientUserIds: [],
    notifyContact: false,
    template: '',
  };
}

/** Form values → create body. */
export function toCreateRequest(v: RuleFormValues): Schemas['CreateReminderRuleRequest'] {
  const template = v.template.trim();
  return {
    name: v.name.trim(),
    kind: v.kind,
    isActive: v.isActive,
    offsetDays: KINDS[v.kind].timing === 'none' ? 0 : v.offsetDays,
    repeatEveryDays: v.repeatEveryDays,
    channels: v.channels,
    recipientRoleIds: v.recipientRoleIds,
    recipientUserIds: v.recipientUserIds,
    notifyContact: KINDS[v.kind].contact ? v.notifyContact : false,
    template: template === '' ? null : template,
  };
}

/** Form values → PATCH body (kind cannot change). */
export function toUpdateRequest(
  v: RuleFormValues,
  version: number,
): Schemas['UpdateReminderRuleRequest'] {
  const body: Partial<Schemas['CreateReminderRuleRequest']> = toCreateRequest(v);
  delete body.kind;
  return { ...(body as Omit<Schemas['CreateReminderRuleRequest'], 'kind'>), version };
}

/** Placeholders used in a template that the kind does not offer. */
export function unknownPlaceholders(template: string, allowed: string[]): string[] {
  const used = [...template.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1] ?? '');
  return [...new Set(used.filter((u) => !allowed.includes(u)))];
}

/** Maps API error details (`body.offsetDays`) to form field names. */
export function apiFieldPath(path: string): keyof RuleFormInput | null {
  const field = path.replace(/^body\./, '').split('.')[0] ?? '';
  const known: (keyof RuleFormInput)[] = [
    'name',
    'kind',
    'offsetDays',
    'repeatEveryDays',
    'channels',
    'recipientRoleIds',
    'recipientUserIds',
    'notifyContact',
    'template',
  ];
  return (known as string[]).includes(field) ? (field as keyof RuleFormInput) : null;
}
